import { PageIcon } from "@/components/PageIcon";
import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pill } from "@/components/StatusPill";
import { Loader2, Inbox, RefreshCw, UserPlus, Search, MessageSquare } from "lucide-react";

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function CommunicationsInbox() {
  const { can } = useAuth();
  const mayManage = can("communications.manage");

  const [loading, setLoading] = useState(true);
  const [convs, setConvs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);

  // assign form
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [chosen, setChosen] = useState(null);
  const [jobId, setJobId] = useState("");
  const [assigning, setAssigning] = useState(false);

  const loadConvs = useCallback(async () => {
    try {
      const r = await api.get(`/communications/conversations`, { params: { kind: "unassigned" } });
      setConvs(r.data.items || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadConvs(); }, [loadConvs]);

  const [params] = useSearchParams();
  const convParam = params.get("conversation");
  useEffect(() => {
    if (!convParam) { return; }
    api.get(`/communications/conversations/${convParam}`)
      .then((r) => openConv(r.data.conversation || r.data))
      .catch(() => toast.error("Conversation not found"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convParam]);

  const openConv = async (c) => {
    setSelected(c); setMessages([]); setChosen(null); setQ(""); setResults([]); setJobId("");
    setMsgLoading(true);
    try {
      const r = await api.get(`/communications/conversations/${c.id}/messages`);
      setMessages(r.data.items || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setMsgLoading(false); }
  };

  const search = async () => {
    if (q.trim().length < 2) { toast.error("Type at least 2 characters"); return; }
    setSearching(true);
    try {
      const r = await api.get(`/communications/customer-search`, { params: { q } });
      setResults(r.data.items || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSearching(false); }
  };

  const assign = async () => {
    if (!chosen) { toast.error("Choose a customer first"); return; }
    setAssigning(true);
    try {
      await api.post(`/communications/conversations/${selected.id}/assign`, {
        customer_id: chosen.legacy_id, job_id: jobId.trim() || null,
      });
      toast.success(`Linked to ${chosen.full_name}`);
      setConvs((s) => s.filter((c) => c.id !== selected.id));
      setSelected(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setAssigning(false); }
  };

  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="comms-inbox-page">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3"><PageIcon /> Unassigned Inbox</h1>
          <p className="text-sm text-slate-500 mt-1">Incoming SMS replies that could not be matched to a customer or job. Identify the customer and link the conversation.</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadConvs} className="gap-1.5" data-testid="comms-inbox-refresh"><RefreshCw className="w-4 h-4" /> Refresh</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Conversation list */}
        <div className="lg:col-span-1 bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 text-sm font-bold text-slate-900 flex items-center justify-between">
            Pending <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold" data-testid="comms-inbox-count">{convs.length}</span>
          </div>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-400 py-10 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : convs.length === 0 ? (
            <div className="text-center text-sm text-slate-400 py-10" data-testid="comms-inbox-empty">Inbox is clear 🎉</div>
          ) : (
            <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
              {convs.map((c) => (
                <button key={c.id} onClick={() => openConv(c)} data-testid={`comms-inbox-item-${c.id}`}
                  className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors ${selected?.id === c.id ? "bg-indigo-50" : ""}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-900">{c.unassigned_from || c.to_number || "Unknown"}</span>
                    <Pill colour="amber" dot={false}>pending</Pill>
                  </div>
                  <div className="text-[11px] text-slate-400 mt-1">to {c.unassigned_to || "—"} · {fmt(c.last_message_at || c.created_at)}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Detail + assign */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
          {!selected ? (
            <div className="text-center text-sm text-slate-400 py-16" data-testid="comms-inbox-detail-empty">
              <MessageSquare className="w-8 h-8 mx-auto mb-3 text-slate-300" />
              Select a conversation to view messages and assign it.
            </div>
          ) : (
            <div data-testid="comms-inbox-detail">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-base font-bold text-slate-900" data-testid="comms-inbox-detail-from">{selected.unassigned_from || selected.to_number || "Customer"}</div>
                  {selected.unassigned_to && <div className="text-[11px] text-slate-400">received on {selected.unassigned_to}</div>}
                </div>
              </div>

              <div className="max-h-72 overflow-y-auto space-y-3 mb-5 pr-1 border rounded-xl border-slate-100 p-3" data-testid="comms-inbox-messages">
                {msgLoading ? (
                  <div className="flex items-center gap-2 text-sm text-slate-400 py-6 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
                ) : messages.length === 0 ? (
                  <div className="text-center text-sm text-slate-400 py-6">No messages.</div>
                ) : messages.map((m) => (
                  <div key={m.id} className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 border ${m.direction === "outbound" ? "bg-indigo-50 border-indigo-100" : "bg-slate-50 border-slate-200"}`}>
                      <div className="text-sm text-slate-800 whitespace-pre-wrap break-words">{m.body}</div>
                      <div className="text-[11px] text-slate-400 mt-1.5">{m.direction === "outbound" ? `To ${m.to_number}` : `From ${m.from_number}`} · {fmt(m.created_at)}</div>
                    </div>
                  </div>
                ))}
              </div>

              {mayManage ? (
                <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid="comms-inbox-assign">
                  <div className="text-sm font-semibold text-slate-900 flex items-center gap-1.5"><UserPlus className="w-4 h-4 text-indigo-500" /> Link to a customer</div>
                  <div className="flex gap-2">
                    <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Search name, company or number…" data-testid="comms-inbox-search-input" />
                    <Button variant="outline" onClick={search} disabled={searching} className="gap-1.5" data-testid="comms-inbox-search-btn">
                      {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Search
                    </Button>
                  </div>
                  {results.length > 0 && (
                    <div className="border rounded-lg border-slate-100 divide-y divide-slate-100 max-h-44 overflow-y-auto">
                      {results.map((r) => (
                        <button key={r.legacy_id} onClick={() => setChosen(r)} data-testid={`comms-inbox-result-${r.legacy_id}`}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${chosen?.legacy_id === r.legacy_id ? "bg-indigo-50" : ""}`}>
                          <span className="font-medium text-slate-900">{r.full_name}</span>
                          {r.company ? <span className="text-slate-400"> · {r.company}</span> : null}
                          <span className="text-[11px] text-slate-400 block">{r.mobile || r.telephone || ""}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                    <Input value={jobId} onChange={(e) => setJobId(e.target.value)} placeholder="Job ID (optional)" className="sm:w-48" data-testid="comms-inbox-job-input" />
                    <Button className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white sm:ml-auto" disabled={!chosen || assigning} onClick={assign} data-testid="comms-inbox-assign-btn">
                      {assigning ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Assign{chosen ? ` to ${chosen.full_name}` : ""}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-slate-400 text-center py-2">You need the manage permission to assign conversations.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
